import { inputAnatomy } from '@chakra-ui/anatomy';
import { createMultiStyleConfigHelpers } from '@chakra-ui/react';

const { definePartsStyle, defineMultiStyleConfig } =
  createMultiStyleConfigHelpers(inputAnatomy.keys);

const baseStyle = definePartsStyle({
  field: {
    bg: 'transparent',
    color: 'input.font',
    borderColor: 'input.border!',
    boxShadow: 'none',
    _placeholder: {
      color: 'rgba(255, 255, 255, 0.78)',
      opacity: 1,
    },
    _hover: {
      borderColor: 'input.border-hover!',
      boxShadow: 'none',
    },
    _focus: {
      borderColor: 'input.border-filled!',
      boxShadow: 'none',
    },
    _invalid: {
      borderColor: 'input.border-error!',
      boxShadow: 'none',
      _hover: {
        borderColor: 'input.border-error!',
        boxShadow: 'none',
      },
      _focus: {
        borderColor: 'input.border-error!',
        boxShadow: 'none',
      },
    },
    _focusVisible: {
      borderColor: 'input.border-filled!',
      boxShadow: 'none',
    },
  },
  addon: {
    bg: 'transparent',
    borderColor: 'input.border',
    color: 'input.font',
  },
  element: {
    color: 'input.icon',
  },
});

// Variants for different states
const variants = {
  outline: definePartsStyle({
    field: {
      bg: 'glass-items.bg',
      color: 'glass-items.icon',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'glass-items.border!',
      boxShadow: 'none!',
      _hover: {
        bg: 'cards.bg-hover',
        borderColor: 'glass-items.icon-hover!',
      },
      _focus: {
        bg: 'glass-items.bg-active',
        borderColor: 'glass-items.border!',
      },
    },
  }),
  filled: definePartsStyle({
    field: {
      bg: 'transparent',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'input.border',
      boxShadow: 'none',
      _hover: {
        bg: 'transparent',
        borderColor: 'input.border-hover',
        boxShadow: 'none',
      },
      _focus: {
        bg: 'transparent',
        borderColor: 'input.border-filled',
        boxShadow: 'none',
      },
    },
  }),
  flushed: definePartsStyle({
    field: {
      bg: 'transparent',
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderBottomColor: 'input.border!',
      borderTopWidth: 0,
      borderLeftWidth: 0,
      borderRightWidth: 0,
      borderRadius: 0,
      px: 0,
      boxShadow: 'none!',
      _hover: {
        bg: 'transparent',
        borderBottomColor: 'input.border-hover!',
      },
      _focus: {
        bg: 'transparent',
        borderBottomColor: 'input.border-filled!',
      },
    },
  }),
  unstyled: definePartsStyle({
    field: {
      bg: 'transparent',
      borderWidth: 0,
      boxShadow: 'none',
    },
  }),
  success: definePartsStyle({
    field: {
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'input.border-success',
      boxShadow: 'none',
      _hover: {
        borderColor: 'input.border-success',
        boxShadow: 'none',
      },
      _focus: {
        borderColor: 'input.border-success',
        boxShadow: 'none',
      },
    },
  }),
  warning: definePartsStyle({
    field: {
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'input.border-warning',
      boxShadow: 'none',
      _hover: {
        borderColor: 'input.border-warning',
        boxShadow: 'none',
      },
      _focus: {
        borderColor: 'input.border-warning',
        boxShadow: 'none',
      },
    },
  }),
  info: definePartsStyle({
    field: {
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'input.border-info',
      boxShadow: 'none',
      _hover: {
        borderColor: 'input.border-info',
        boxShadow: 'none',
      },
      _focus: {
        borderColor: 'input.border-info',
        boxShadow: 'none',
      },
    },
  }),
};

// Size variants
const sizes = {
  sm: definePartsStyle({
    field: {
      fontSize: '16px',
      lineHeight: '24px',
      py: 2,
      px: 2.5,
      h: 10,
      borderRadius: 'lg',
    },
    element: {
      h: 10,
    },
  }),
  md: definePartsStyle({
    field: {
      fontSize: '16px',
      lineHeight: '24px',
      py: 3,
      px: 3.5,
      h: 10,
      borderRadius: 'lg',
    },
    element: {
      h: 10,
    },
  }),
  lg: definePartsStyle({
    field: {
      fontSize: '16px',
      lineHeight: '24px',
      py: 2,
      px: 4,
      h: 12,
      borderRadius: 'xl',
    },
    element: {
      h: 12,
    },
  }),
};

export const inputTheme = defineMultiStyleConfig({
  baseStyle,
  variants,
  sizes,
  defaultProps: {
    variant: 'outline',
    size: 'md',
  },
});

// Also export as Field theme for saas-ui compatibility
export const fieldTheme = inputTheme;
